<template>
  <SimpleModal
    title="c.25110162"
    class="send-message-modal"
    size="middle"
    :class="{ 'show-dept': showDept }"
    okText="c.25110167"
    :confirmLoading="confirmLoading"
    @register="registerModal"
    @cancel="handleModalClose"
    @ok="handleOk"
  >
    <div class="flex h-full overflow-hidden">
      <div class="w-500px h-full overflow-auto">
        <BasicForm ref="simpleFormRef" />
      </div>
      <div v-show="showDept" class="h-full flex-1">
        <BasicTree
          :title="t('c.25110073')"
          toolbar
          search
          checkable
          :treeData="deptTreeData"
          :fieldNames="{ children: 'children', title: 'name', key: 'id' }"
          @check="onDeptChecked"
        />
      </div>
    </div>
  </SimpleModal>
</template>

<script setup lang="ts">
  import { message } from 'ant-design-vue';
  import { pick, uniq, without } from 'lodash-es';
  import { h, nextTick, onMounted, ref, watch } from 'vue';
  import { BasicForm, FormActionType } from '@mts/components/Form';
  import { SimpleModal, useModal } from '@mts/components/Modal';
  import { BasicTree, TreeItem } from '@mts/components/Tree';
  import { useI18n } from '@mts/hooks/web/useI18n';
  import { BaseFormProps, createApi, useBaseSchema } from '@mts/render';
  import { useGlobalStore } from '@/store/modules/global';

  const { t } = useI18n();
  defineOptions({ name: 'SendMessageModal' });
  const globalStore = useGlobalStore();
  const [registerModal, modalAction] = useModal();
  const simpleFormRef = ref<FormActionType>();
  const confirmLoading = ref(false);

  const { getFormPropsByCode } = useBaseSchema('CF251118001', async (schema) => {
    const formProps = schema.formList?.find((item) => item._ID === 'FORM') as BaseFormProps;
    formProps.schemas?.forEach((formItem) => {
      if (formItem.field === 'userIds') {
        const _label = formItem.label as string;
        formItem.label = () => {
          return h('span', {}, [
            t(_label),
            h('span', {}, `(${userIdLength.value})`),
            h(
              'span',
              {
                type: 'link',
                size: 'small',
                class: 'text-[#01a887] cursor-pointer ml-8px',
                onClick: checkShowDept,
              },
              '组织结构',
            ),
          ]);
        };
      }
    });
  });

  const handleModalClose = () => {
    useGlobalStore().updateSendMessageModal(false, {});
  };

  interface SendMessageModalOptions {
    templateCode?: string;
    bizParam?: Record<string, any>;
    successCb?: (res: any) => void;
  }
  // 获取模版信息
  const setModuleFormData = async () => {
    const res = await createApi('/ims-system/message/sendMessageByTemplate')({
      ...pick(optionClone.value, ['templateCode', 'bizParam']),
      realSend: false,
    });

    simpleFormRef.value?.setFieldsValue({
      ...res,
      userIds: res.userIds || res.notifier || [],
      extendInfo: res?.extendInfo || '',
    });
    onFieldValueChange('userIds', res.userIds || res.notifier || []);
  };

  const userIdLength = ref<number>(0);
  const onFieldValueChange = (field: string, values: string[]) => {
    if (field === 'userIds') {
      userIdLength.value = values?.length || 0;
    }
  };

  const optionClone = ref<SendMessageModalOptions>();
  const openSendMessageModal = async (options: SendMessageModalOptions) => {
    // 后期需要校验该模板是否启用
    modalAction.openModal(true);
    optionClone.value = options;
    getDeptTree();
    await nextTick();
    const formPropsClone = getFormPropsByCode('FORM');
    simpleFormRef.value?.setProps(formPropsClone as BaseFormProps);
    setModuleFormData();
  };

  const showDept = ref(false);
  const checkShowDept = () => {
    showDept.value = !showDept.value;
  };

  const handleOk = async () => {
    try {
      const values = await simpleFormRef.value?.validate();
      confirmLoading.value = true;
      const res = await createApi('/ims-system/message/sendMessageByTemplate')({
        ...pick(optionClone.value, ['templateCode', 'bizParam', 'type', 'bizType']), // type, bizType 为无 templateCode 时必传
        ...values,
        realSend: true,
        rememberNotifier: true,
      });
      optionClone.value?.successCb?.(res);
      modalAction.closeModal();
      useGlobalStore().updateSendMessageModal(false, {});
      message.success(t('c.25110226'));
    } catch (error) {
      console.error('-------------- send message handleOk: ', error);
    } finally {
      confirmLoading.value = false;
    }
  };

  const deptTreeData = ref<TreeItem[]>([]);
  const getDeptTree = async () => {
    const res = await createApi('/ims-auth/sysDept/list-tree-all')({});
    deptTreeData.value = res || [];
  };
  const onDeptChecked = async (_checkedKeys: string[], e) => {
    if (e.node.id) {
      const res = await createApi('/ims-auth/sysUser/listForSelect')({
        deptId: e.node.id,
        personnelStatus: 0,
        page: 1,
        size: 9999,
      });
      if (res?.length) {
        const formValues = simpleFormRef.value!.getFieldsValue();
        const newUserIds = e.checked
          ? uniq([...(formValues.userIds || []), ...res.map((item) => item.id)])
          : without(formValues.userIds || [], ...res.map((item) => item.id));
        simpleFormRef.value?.setFieldsValue({ userIds: newUserIds });
        onFieldValueChange('userIds', newUserIds);
      }
    }
  };

  onMounted(() => {
    watch(
      () => globalStore.sendMessageModal.open,
      (val, prevVal) => {
        val && !prevVal && openSendMessageModal(globalStore.sendMessageModal.options);
      },
      { deep: true, immediate: true },
    );
  });
</script>

<style lang="less">
  .send-message-modal {
    width: 500px !important;
    transition: width 0.3s ease-in-out;

    &.show-dept {
      width: 1000px !important;
    }
  }
</style>
